import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  RemittanceWorksheet,
  WithholdingRemittance,
  WithholdingRemittanceChargesCommand,
  WithholdingRemittancePostConfirm,
} from '../models/withholding-remittance/withholding-remittance';

/**
 * Monthly withholding remittance to SAR (#4489). Review the period worksheet, build the draft, then post it —
 * Dr Retenciones por Pagar / Cr Bank — which pays the withheld tax and flags each retention as remitted.
 */
@Injectable({ providedIn: 'root' })
export class WithholdingRemittanceProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/withholding-remittances';

  /** Period preview: every withholdable AP doc posted in the month with its retention status. */
  public getWorksheet(year: number, month: number): Observable<ApiResponse<RemittanceWorksheet>> {
    const params = new HttpParams().set('year', year).set('month', month);
    return this.httpClient.get<ApiResponse<RemittanceWorksheet>>(`${this.baseUrl}/worksheet`, { params });
  }

  public getById(id: string): Observable<ApiResponse<WithholdingRemittance>> {
    return this.httpClient.get<ApiResponse<WithholdingRemittance>>(`${this.baseUrl}/${id}`);
  }

  /** Creates or refreshes the draft remittance for a period (seeded with its posted, un-remitted retentions). */
  public build(year: number, month: number): Observable<ApiResponse<WithholdingRemittance>> {
    const params = new HttpParams().set('year', year).set('month', month);
    return this.httpClient.post<ApiResponse<WithholdingRemittance>>(`${this.baseUrl}/build`, {}, { params });
  }

  /** Posts the remittance (pays SAR): Dr Retenciones / Cr Bank, stamps each retention remitted. */
  /**
   * Replaces the charges SAR added to a draft (#5029) — late penalty, surcharge, interest.
   *
   * A replace rather than a patch: the officer edits a short list on a form, and sending the whole list is
   * what that produces. It also makes deletion expressible without a second verb.
   */
  public saveCharges(id: string, command: WithholdingRemittanceChargesCommand): Observable<ApiResponse<WithholdingRemittance>> {
    return this.httpClient.put<ApiResponse<WithholdingRemittance>>(`${this.baseUrl}/${id}/charges`, command);
  }

  public post(id: string, confirm: WithholdingRemittancePostConfirm): Observable<ApiResponse<WithholdingRemittance>> {
    return this.httpClient.post<ApiResponse<WithholdingRemittance>>(`${this.baseUrl}/${id}/post`, confirm);
  }
}
